import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { DocMessages } from "../messages/MessagePages";
import { Link, useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import QRCode from "qrcode";
import { vouchersApi, type DocLook, type InvoicePrint, type Party } from "../lib/api";
import { useCompany } from "../masters/reference";
import { errText } from "../ui/useLoad";
import { ErrorNote, Loading } from "../ui/bits";
import { fmtAmount } from "../lib/money";
import { amountInWords } from "../lib/india";
import { fmtDate } from "../companies/CompanyPicker";
import { toUrl } from "../shell/routes";
import { TERMS_LABEL } from "./entry";

// The sale as a document to hand over: tax invoice, bill of supply or invoice-cum-bill of supply, with the particulars of
// rule 46 (or 49): supplier and recipient with GSTIN and state, number and date, place of supply, HSN, quantity, value,
// rate and tax by head, total in figures and words, reverse charge, the declarations, IRN and e-way bill. It wears the
// company's look (sql/224): layout, colour, logo, the company's own fields, HSN summary, bank details and a UPI QR to
// pay, terms, signature. Printed from the browser (or saved as PDF); the screen chrome is hidden on paper.
const RECIPIENT: Record<string, string> = { credit_note: "Issued to", purchase_order: "To", quote: "Quote for", proforma: "Billed to", challan: "Consignee", sales_order: "Customer" };
export const COPIES = ["Original for recipient", "Duplicate for transporter", "Triplicate for supplier"];
const DEFAULT_LOOK: DocLook = { template: "classic", accent: "#4338CA", paper: "A4", show_hsn_summary: true, show_pay_qr: true };

function Addr({ p, label }: { p: Party; label: string }) {
  return (
    <div className="doc-party">
      <div className="doc-label">{label}</div>
      <div className="doc-name">{p.name}</div>
      {p.legal_name && p.legal_name !== p.name && <div className="small">{p.legal_name}</div>}
      {p.address && <div>{p.address}</div>}
      {(p.place || p.pincode) && <div>{[p.place, p.pincode].filter(Boolean).join(" ")}</div>}
      {p.state && <div>State: {p.state} ({p.state_code})</div>}
      <div>{p.gstin ? <>GSTIN <b className="mono">{p.gstin}</b></> : "Unregistered"}</div>
    </div>
  );
}

function useQr(text: string | null | undefined) {
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    if (!text) { setQr(null); return; }
    let on = true;
    QRCode.toDataURL(text, { margin: 1, width: 240, errorCorrectionLevel: "M" }).then((x) => { if (on) setQr(x); }).catch(() => { if (on) setQr(null); });
    return () => { on = false; };
  }, [text]);
  return qr;
}

// the document itself: used by the print page and by the preview in Settings › Invoice look
export function InvoiceDoc({ d, copy = 0, look: lookOverride }: { d: InvoicePrint; copy?: number; look?: DocLook }) {
  const look = lookOverride ?? d.look ?? DEFAULT_LOOK;
  // the IRP's signed QR on an e-invoice; the dynamic UPI QR on a B2C invoice above ₹500 crore turnover; otherwise the
  // company's UPI QR to pay, when it has a UPI ID and has it turned on
  const irnQr = useQr(d.irn?.signed_qr ?? d.b2c_qr?.text);
  const payQr = useQr(look.show_pay_qr && !d.irn && !d.b2c_qr && d.pay_qr ? d.pay_qr : null);

  const bos = d.document.kind === "bill_of_supply";
  const taxCols = !bos && d.lines.some((l) => (l.cgst ?? 0) + (l.sgst ?? 0) + (l.igst ?? 0) + (l.cess ?? 0) > 0);
  // integrated tax: charged on a line, or a supply to another state or abroad (an export under LUT shows IGST nil)
  const igst = d.lines.some((l) => (l.igst ?? 0) > 0) || (!!d.place_of_supply && d.place_of_supply.code !== d.supplier.state_code);
  const sum = (k: "taxable" | "cgst" | "sgst" | "igst" | "cess") => d.lines.reduce((t, l) => t + (l[k] ?? 0), 0);
  const tax = sum("cgst") + sum("sgst") + sum("igst") + sum("cess");
  const n = (x: number | undefined | null) => (x == null ? "" : fmtAmount(x));
  const sym = d.fx ? d.currency : "₹";   // a foreign-currency invoice is in its currency, with the rupee values below (sql/019)
  const hsn = look.show_hsn_summary && taxCols && !d.fx ? d.hsn_summary ?? [] : [];
  const bank = look.bank && Object.values(look.bank).some(Boolean) ? look.bank : null;
  const discCol = d.lines.some((l) => l.discount && l.discount.value > 0);
  // before the document's own discount: each line's quantity × rate less its discount (the taxable values are after it)
  const lineNet = (l: (typeof d.lines)[number]) => l.qty != null && l.rate != null ? Math.round((l.qty * l.rate - (l.discount ? (l.discount.percent ? l.qty * l.rate * l.discount.value / 100 : l.discount.value) : 0)) * 100) / 100 : l.taxable;
  const preDisc = d.discount ? d.lines.reduce((t, l) => t + lineNet(l), 0) : 0;

  return (
    <article className={`doc t-${look.template} paper-${look.paper}`} style={{ "--doc-accent": look.accent } as CSSProperties}>
      <style>{`@media print { @page { size: ${look.paper === "Letter" ? "letter" : look.paper}; margin: ${look.paper === "A5" ? "8mm" : "12mm"}; } }`}</style>
      <header className="doc-head">
        <div className="doc-from">
          {look.logo && <img className="doc-logo" src={look.logo} alt="" />}
          <div>
            <div className="doc-supplier">{d.supplier.name}</div>
            {d.supplier.legal_name && d.supplier.legal_name !== d.supplier.name && <div className="small">{d.supplier.legal_name}</div>}
            <div className="small">{[d.supplier.address, d.supplier.place, d.supplier.pincode].filter(Boolean).join(", ")}</div>
            <div className="small">{d.supplier.gstin && <>GSTIN <b className="mono">{d.supplier.gstin}</b>{d.supplier.state ? " · " : ""}</>}{d.supplier.state && <>{d.supplier.state}{d.supplier.state_code ? ` (${d.supplier.state_code})` : ""}</>}</div>
          </div>
        </div>
        <div className="doc-title">
          <h1>{d.document.title}</h1>
          {!d.order_id && <div className="small muted">{COPIES[copy]}</div>}
          {d.accepted && <div className="doc-accepted">Accepted {fmtDate(d.accepted.at.slice(0, 10))} by {d.accepted.by}</div>}
        </div>
      </header>
      {d.document.declarations.length > 0 && <div className="doc-declare">{d.document.declarations.map((x) => <div key={x}>{x}</div>)}</div>}

      <div className="doc-meta">
        <Addr p={d.recipient} label={RECIPIENT[d.document.kind] ?? "Billed to"} />
        <div className="doc-facts">
          <div><span>Number</span><b className="mono">{d.number ?? "draft"}</b></div>
          <div><span>Date</span><b>{fmtDate(d.date)}</b></div>
          {d.place_of_supply && <div><span>Place of supply</span><b>{d.place_of_supply.name} ({d.place_of_supply.code})</b></div>}
          {d.valid_until && <div><span>Valid until</span><b>{fmtDate(d.valid_until)}</b></div>}
          {d.order_number && !d.order_id && <div><span>Order number</span><b>{d.order_number}</b></div>}
          {d.terms_label && d.terms_label !== "custom" && <div><span>Terms</span><b>{TERMS_LABEL[d.terms_label] ?? d.terms_label}</b></div>}
          {d.salesperson && <div><span>Salesperson</span><b>{d.salesperson}</b></div>}
          {d.reference_no && <div><span>{d.order_id ? "Reference" : "Buyer's order"}</span><b>{d.reference_no}{d.reference_date ? `, ${fmtDate(d.reference_date)}` : ""}</b></div>}
          {d.original && <div><span>Against invoice</span><b>{d.original.number}, {fmtDate(d.original.date)}</b></div>}
          {d.due_date && <div><span>{d.order_id ? "Expected by" : "Due"}</span><b>{fmtDate(d.due_date)}</b></div>}
          {!d.order_id && (d.supplier.gstin || d.reverse_charge) && <div><span>Reverse charge</span><b>{d.reverse_charge ? "Yes" : "No"}</b></div>}
          {d.eco && <div><span>Through</span><b>{d.eco.name} ({d.eco.gstin})</b></div>}
          {d.eway_bill_no && <div><span>E-way bill</span><b className="mono">{d.eway_bill_no}</b></div>}
          {(d.fields ?? []).map((f) => <div key={f.label}><span>{f.label}</span><b>{f.kind === "date" ? fmtDate(f.value) : f.value}</b></div>)}
        </div>
      </div>
      {d.subject && <div className="doc-subject"><span className="doc-label">Subject</span> {d.subject}</div>}
      {d.ship_to && <div className="doc-shipto small"><b>Shipped to:</b> {[d.ship_to.place, d.ship_to.pincode, d.ship_to.state].filter(Boolean).join(", ")}</div>}

      <table className="doc-lines">
        <thead><tr><th>#</th><th>Description</th><th>HSN/SAC</th><th className="num">Qty</th><th className="num">Rate</th>{discCol && <th className="num">Discount</th>}<th className="num">{bos ? "Value" : "Taxable value"}</th>
          {taxCols && <><th className="num">GST %</th>{igst ? <th className="num">IGST</th> : <><th className="num">CGST</th><th className="num">SGST</th></>}</>}</tr></thead>
        <tbody>{d.lines.map((l) => (
          <tr key={l.no}>
            <td>{l.no}</td>
            <td>{l.item_name ?? l.description}{l.taxability && l.taxability !== "taxable" && <span className="muted small"> ({l.taxability.replace("_", "-")})</span>}
              {l.narration && <div className="muted small">{l.narration}</div>}</td>
            <td className="mono">{l.hsn_sac ?? ""}</td>
            <td className="num">{l.qty != null ? `${l.qty.toLocaleString("en-IN")} ${l.unit ?? ""}` : ""}</td>
            <td className="num">{n(l.rate)}</td>
            {discCol && <td className="num">{l.discount && l.discount.value > 0 ? (l.discount.percent ? `${l.discount.value}%` : n(l.discount.value)) : ""}</td>}
            <td className="num">{n(l.taxable)}</td>
            {taxCols && <><td className="num">{l.taxability === "taxable" ? `${l.gst_rate}%` : "—"}</td>
              {igst ? <td className="num">{n(l.igst)}</td> : <><td className="num">{n(l.cgst)}</td><td className="num">{n(l.sgst)}</td></>}</>}
          </tr>))}</tbody>
      </table>

      <div className="doc-totals">
        <div className="doc-words">
          <div className="doc-label">Amount in words</div>
          <div>{amountInWords(d.total, d.currency)}</div>
          {d.reverse_charge && tax > 0 && <div className="small">Tax of {sym}{fmtAmount(tax)} is payable by the recipient under reverse charge.</div>}
          {d.fx && <div className="small">At {Number(d.fx.rate).toLocaleString("en-IN", { maximumFractionDigits: 6 })} {d.fx.base_currency} per {d.currency}: taxable value {d.fx.base_currency} {n(d.fx.taxable)}
            {d.fx.tax ? `, tax ${d.fx.base_currency} ${n(d.fx.tax)}` : ""}, total {d.fx.base_currency} {n(d.fx.total)}.</div>}
        </div>
        <table>
          <tbody>
            {d.discount && preDisc > sum("taxable") && <><tr><td>Sub total</td><td className="num">{n(preDisc)}</td></tr>
              <tr><td>Discount{d.discount.percent ? ` (${d.discount.value}%)` : ""}</td><td className="num">−{n(Math.round((preDisc - sum("taxable")) * 100) / 100)}</td></tr></>}
            <tr><td>{bos ? "Total value" : "Taxable value"}</td><td className="num">{n(sum("taxable"))}</td></tr>
            {!bos && !d.reverse_charge && (d.supplier.gstin || tax > 0) && (igst ? <tr><td>IGST</td><td className="num">{n(sum("igst"))}</td></tr>
              : <><tr><td>CGST</td><td className="num">{n(sum("cgst"))}</td></tr><tr><td>SGST / UTGST</td><td className="num">{n(sum("sgst"))}</td></tr></>)}
            {!bos && !d.reverse_charge && sum("cess") > 0 && <tr><td>Cess</td><td className="num">{n(sum("cess"))}</td></tr>}
            {d.reverse_charge && tax > 0 && <tr className="muted"><td>Tax under reverse charge</td><td className="num">{n(tax)}</td></tr>}
            {d.round_off ? <tr><td>Round off</td><td className="num">{n(d.round_off)}</td></tr> : null}
            <tr className="grand"><td>Total</td><td className="num">{sym} {n(d.total)}</td></tr>
          </tbody>
        </table>
      </div>

      {hsn.length > 1 || (hsn.length === 1 && d.lines.length > 1) ? (
        <table className="doc-lines doc-hsn">
          <thead><tr><th>HSN/SAC</th><th className="num">GST %</th><th className="num">Taxable value</th>
            {igst ? <th className="num">IGST</th> : <><th className="num">CGST</th><th className="num">SGST</th></>}<th className="num">Total tax</th></tr></thead>
          <tbody>{hsn.map((h) => (
            <tr key={`${h.hsn_sac}-${h.rate}`}><td className="mono">{h.hsn_sac}</td><td className="num">{h.rate}%</td><td className="num">{n(h.taxable)}</td>
              {igst ? <td className="num">{n(h.igst)}</td> : <><td className="num">{n(h.cgst)}</td><td className="num">{n(h.sgst)}</td></>}
              <td className="num">{n(h.cgst + h.sgst + h.igst + h.cess)}</td></tr>))}</tbody>
        </table>
      ) : null}

      {(d.irn || irnQr) && <div className="doc-qr">
        {irnQr && <img src={irnQr} alt={d.irn ? "Signed QR code of the e-invoice" : "Payment QR code"} />}
        <div className="doc-irn small">
          {d.irn ? <><div><b>IRN</b> <span className="mono">{d.irn.irn}</span></div>
            <div>Ack no. {d.irn.ack_no}{d.irn.ack_at ? ` · ${new Date(d.irn.ack_at).toLocaleString("en-IN")}` : ""}</div></>
            : <div>Scan to pay by UPI{d.b2c_qr?.upi_id ? ` (${d.b2c_qr.upi_id})` : ""}</div>}
        </div></div>}

      {(bank || payQr) && (
        <div className="doc-pay">
          {bank && <div className="doc-bank">
            <div className="doc-label">Pay to</div>
            {bank.account_name && <div><b>{bank.account_name}</b></div>}
            {bank.account_no && <div>A/c no. <span className="mono">{bank.account_no}</span></div>}
            {bank.ifsc && <div>IFSC <span className="mono">{bank.ifsc}</span>{bank.swift ? <> · SWIFT <span className="mono">{bank.swift}</span></> : null}</div>}
            {(bank.bank || bank.branch) && <div>{[bank.bank, bank.branch].filter(Boolean).join(", ")}</div>}
            {look.upi_id && <div>UPI <span className="mono">{look.upi_id}</span></div>}
          </div>}
          {payQr && <div className="doc-payqr"><img src={payQr} alt="UPI QR code to pay this invoice" /><div className="small">Scan to pay {sym}{n(d.total)} by UPI</div></div>}
        </div>
      )}
      {d.notes ? <div className="doc-notes small"><div className="doc-label">Notes</div>{d.notes.split("\n").map((t, i) => <div key={i}>{t}</div>)}</div>
        : d.narration && <p className="small"><b>Notes:</b> {d.narration}</p>}
      {look.terms && <div className="doc-terms small"><div className="doc-label">Terms and conditions</div>{look.terms.split("\n").map((t, i) => <div key={i}>{t}</div>)}</div>}
      <footer className="doc-foot">
        <div className="small muted">{look.footer ?? "This is a computer-generated document."}</div>
        <div className="doc-sign">For {d.supplier.legal_name ?? d.supplier.name}
          {look.signature ? <img className="doc-signature" src={look.signature} alt="" /> : <><br /><br /><br /></>}
          <div>{look.signatory ?? "Authorised signatory"}</div></div>
      </footer>
    </article>
  );
}

export function InvoicePrintPage() {
  const { company } = useCompany();
  const { voucherId } = useParams();
  const [d, setD] = useState<InvoicePrint | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copy, setCopy] = useState(0);
  useEffect(() => { vouchersApi.print(company.id, voucherId!).then(setD).catch((e) => setErr(errText(e))); }, [company.id, voucherId]);
  const sendTarget = useMemo(() => ({ voucher_id: voucherId }), [voucherId]);
  if (err) return <section className="page"><ErrorNote text={err} /></section>;
  if (!d) return <section className="page"><Loading /></section>;
  return (
    <section className="page doc-page">
      <div className="doc-tools no-print">
        <Link to={toUrl(`vouchers/v/${voucherId}`)}>← Back to the entry</Link>
        <label className="inline-label">Copy<select value={copy} onChange={(e) => setCopy(Number(e.target.value))}>{COPIES.map((c, i) => <option key={c} value={i}>{c}</option>)}</select></label>
        <button className="primary" onClick={() => window.print()}><Printer size={15} aria-hidden="true" /> Print or save as PDF</button>
        {d.status === "posted" && <DocMessages companyId={company.id} target={sendTarget} />}
        <Link className="small" to={toUrl("company/documents")}>Change the look</Link>
        {d.status !== "posted" && <span className="tag">{d.status}</span>}
      </div>
      {d.einvoice_required && !d.irn && <p className="notice no-print">This invoice needs an IRN from the IRP before it is issued: get it from the e-invoice panel on the entry.</p>}
      {d.b2c_qr && !d.b2c_qr.upi_id && <p className="notice no-print">B2C invoices of this business carry a dynamic QR code: add the UPI ID under Settings › Invoice look.</p>}
      <InvoiceDoc d={d} copy={copy} />
    </section>
  );
}
